import React, { useMemo } from 'react';
import { Col, FormGroup, Label } from 'reactstrap';
import { Field } from 'formik';
import {
   AdvertiserSAAutosuggest,
   Checkbox,
   CurrencyInput, LocationMultiSelectSA,
   SelectInput,
   TextInput,
} from 'ui-blocks/form-elements';
import PropTypes from 'prop-types';
import FieldItem from './FieldItem';
import FieldItemMaxChars from './FieldItemMaxChars';
import FieldItemWithModal from './FieldItemWithModal';
import modalConfig from './modalConfig';
import FieldArrayItem from './FieldArrayItem';
import SampleAdVisualiser from './SampleAdVisualiser';
import FieldRadioItem from './FieldRadioItem';

export const deviceTypeSelectOptions = [
   {
      label: 'All Devices',
      value: 'ALL',
   },
   {
      label: 'Mobile',
      value: 'MOBILE',
   },
   {
      label: 'Desktop',
      value: 'DESKTOP',
   },
];

const approvalRadioOptions = [
   {
      label: 'Approve',
      value: 'APPROVED',
      key: 'approve',
   },
   {
      label: 'Deny',
      value: 'REJECTED',
      key: 'deny',
   },
];

const sectionTitleStyles = {
   color: '#333',
   backgroundColor: '#f5f5f5',
   borderColor: '#ddd',
   padding: '10px 15px',
   borderBottom: '1px solid transparent',
   borderTopLeftRadius: '3px',
   borderTopRightRadius: '3px',
   marginBottom: '1.5em',
};

const visualiserBackgroundStyles = {
   backgroundColor: '#F9FFFF',
   paddingRight: '15px',
   paddingLeft: '15px',
};

const FormDisplay = ({
   formik, fieldArrayIndex, fieldArrayName, showAdvertiserField,
   handleCPAChange, isCpa, showApprovalField, campaignId, rawResourceData,
}) => {
   const getRadiusSelectOptions = useMemo(() => {
      const radiusSelectOptions = [];
      for (let i = 25; i < 101; i += 25) {
         radiusSelectOptions.push({ label: `${ i }`, value: i });
      }
      return radiusSelectOptions;
   }, []);

   const getFieldArrayName = (name) => {
      if (fieldArrayIndex && fieldArrayName) {
         return `${ fieldArrayName }.${ fieldArrayIndex }.${ name }`;
      }
      return name;
   };

   return (
      <>
         <FormGroup row>
            {
               showAdvertiserField ? (
                  <Col xs={12} md={6} lg={3}>
                     <FieldItem
                        label='Advertiser'
                        name={getFieldArrayName('advertiser')}
                        component={AdvertiserSAAutosuggest}
                     />
                  </Col>
               ) : null
            }
            <Col xs={12} md={6} lg={3}>
               <FieldItem
                  label='Device'
                  name={getFieldArrayName('device')}
                  component={SelectInput}
                  options={deviceTypeSelectOptions}
               />
            </Col>
         </FormGroup>
         <FormGroup row>
            <Col xs={12} md={6} lg={3}>
               <FieldItemMaxChars
                  label='Campaign Name'
                  name={getFieldArrayName('campaign')}
                  component={TextInput}
                  maxCharacters={255}
               />
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldItemWithModal
                  label='Keyword'
                  name={getFieldArrayName('keywords')}
                  setValues={formik.setValues}
                  modalConfig={modalConfig}
                  rawResourceData={rawResourceData}
               />
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldItem
                  label={ isCpa ? 'CPA' : 'CPC' }
                  name={getFieldArrayName('paymentTypeValue')}
                  component={CurrencyInput}
               />
               <br/>
               <Field
                  component={Checkbox}
                  onChange={(value) => handleCPAChange(value, campaignId)}
                  value={isCpa}
                  name={getFieldArrayName('paymentType')}
               />
               <Label for={getFieldArrayName('paymentType')} className='ml-2'>Make this campaign CPA</Label>
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldItem
                  label='Daily Budget'
                  name={getFieldArrayName('dailyBudget')}
                  component={CurrencyInput}
               />
            </Col>
         </FormGroup>
         <FormGroup row>
            <Col xs={12} md={6} lg={3}>
               <FieldItem
                  label='Monthly Budget'
                  name={getFieldArrayName('monthlyBudget')}
                  component={CurrencyInput}
               />
            </Col>
         </FormGroup>
         <div style={sectionTitleStyles}>
            <h3 style={ { fontSize: '16px', marginBottom: '0px' } }>Location</h3>
         </div>
         <FormGroup row>
            <Col xs={12} md={6} lg={3}>
               <FieldArrayItem
                  label='Location'
                  name={getFieldArrayName('location')}
                  component={LocationMultiSelectSA}
               />
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldArrayItem
                  label='Exclude'
                  name={getFieldArrayName('exclude')}
                  component={LocationMultiSelectSA}
               />
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldItem
                  label='Radius'
                  name={getFieldArrayName('radius')}
                  component={SelectInput}
                  options={getRadiusSelectOptions}
               />
            </Col>
         </FormGroup>
         <div style={sectionTitleStyles}>
            <h3 style={ { fontSize: '16px', marginBottom: '0px' } }>Ad Creative</h3>
         </div>
         <FormGroup row>
            <Col xs={12} md={6} lg={3}>
               <FieldItemMaxChars
                  label='Title'
                  name={getFieldArrayName('title')}
                  component={TextInput}
                  maxCharacters={75}
               />
               <br/>
               <FieldItemMaxChars
                  label='Text Description'
                  name={getFieldArrayName('line1')}
                  component={TextInput}
                  maxCharacters={95}
                  type='textarea'
               />
               <br/>
               <Field
                  name={getFieldArrayName('active')}
                  component={Checkbox}
               />
               <Label for={getFieldArrayName('active')} className='ml-2'>Active</Label>
            </Col>
            <Col xs={12} md={6} lg={3}>
               <FieldItemMaxChars
                  label='Linking Url'
                  name={getFieldArrayName('linkingUrl')}
                  component={TextInput}
                  maxCharacters={1023}
               />
               <br/>
               <FieldItemMaxChars
                  label='Display Url'
                  name={getFieldArrayName('displayUrl')}
                  component={TextInput}
                  maxCharacters={35}
               />
            </Col>
            <Col xs={12} md={6} lg={3} className='p-0'>
               <div style={visualiserBackgroundStyles}>
                  <p>Sample Ad</p>
                  <SampleAdVisualiser
                     title={
                        fieldArrayIndex && fieldArrayName
                           ? formik.values?.[fieldArrayName]?.[fieldArrayIndex]?.title
                           : formik.values?.title
                     }
                     textDesc={
                        fieldArrayIndex && fieldArrayName
                           ? formik.values?.[fieldArrayName]?.[fieldArrayIndex]?.line1
                           : formik.values?.line1
                     }
                     linkingUrl={
                        fieldArrayIndex && fieldArrayName
                           ? formik.values?.[fieldArrayName]?.[fieldArrayIndex]?.linkingUrl
                           : formik.values?.linkingUrl
                     }
                     displayUrl={
                        fieldArrayIndex && fieldArrayName
                           ? formik.values?.[fieldArrayName]?.[fieldArrayIndex]?.displayUrl
                           : formik.values?.displayUrl
                     }
                  />
               </div>
            </Col>
            { showApprovalField && (
               <Col xs={12} md={6} lg={3}>
                  <FieldRadioItem
                     name={getFieldArrayName('approval')}
                     options={approvalRadioOptions}
                  />
               </Col>
            )}
         </FormGroup>
      </>
   );
};

FormDisplay.propTypes = {
   formik: PropTypes.objectOf(PropTypes.any).isRequired,
   fieldArrayIndex: PropTypes.string,
   fieldArrayName: PropTypes.string,
   campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   showAdvertiserField: PropTypes.bool,
   handleCPAChange: PropTypes.func.isRequired,
   isCpa: PropTypes.bool,
   showApprovalField: PropTypes.bool,
   rawResourceData: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.bool,
   ]),
};

FormDisplay.defaultProps = {
   showAdvertiserField: false,
   showApprovalField: false,
   fieldArrayIndex: '',
   fieldArrayName: '',
   isCpa: false,
   campaignId: 0,
   rawResourceData: false,
};

export default FormDisplay;
